import { useState, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Page,
  Navbar,
  NavbarBackLink,
  Block,
  BlockTitle,
  List,
  ListItem,
  Segmented,
  SegmentedButton,
  Badge,
} from 'konsta/react'
import { Trophy, UserPlus, ArrowUpDown, Info, Gamepad2 } from 'lucide-react'
import { useNotificationStore, type NotificationType } from '@/stores/notificationStore'
import { formatRelativeTime } from '@/lib/utils'

type FilterTab = 'all' | 'jackpot_win' | 'room_invite' | 'transaction' | 'system' | 'game'

const filterLabels: Record<FilterTab, string> = {
  all: '全部',
  jackpot_win: '獎金',
  room_invite: '房間',
  transaction: '交易',
  system: '系統',
  game: '遊戲',
}

const typeIcons: Record<NotificationType, { icon: React.ReactNode; color: string }> = {
  jackpot_win: { icon: <Trophy className="w-5 h-5" />, color: 'text-amber-500 bg-amber-50' },
  room_invite: { icon: <UserPlus className="w-5 h-5" />, color: 'text-[#007AFF] bg-blue-50' },
  transaction: { icon: <ArrowUpDown className="w-5 h-5" />, color: 'text-[#34C759] bg-green-50' },
  system:      { icon: <Info className="w-5 h-5" />, color: 'text-[#8E8E93] bg-gray-100' },
  game:        { icon: <Gamepad2 className="w-5 h-5" />, color: 'text-purple-500 bg-purple-50' },
}

export function NotificationHistoryScreen() {
  const navigate = useNavigate()
  const [tab, setTab] = useState<FilterTab>('all')
  const history = useNotificationStore((s) => s.history)
  const markAsRead = useNotificationStore((s) => s.markAsRead)

  const filtered = useMemo(() => {
    if (tab === 'all') return history
    return history.filter((n) => n.type === tab)
  }, [tab, history])

  return (
    <Page className="bg-[#F2F2F7]">
      <Navbar
        title="通知紀錄"
        left={<NavbarBackLink onClick={() => navigate(-1)} />}
      />

      <Block className="!mt-4 !mb-2">
        <Segmented strong rounded>
          {(Object.keys(filterLabels) as FilterTab[]).map((key) => (
            <SegmentedButton
              key={key}
              active={tab === key}
              onClick={() => setTab(key)}
            >
              <span className="text-xs">{filterLabels[key]}</span>
            </SegmentedButton>
          ))}
        </Segmented>
      </Block>

      {filtered.length === 0 ? (
        <Block className="text-center py-12">
          <p className="text-[#8E8E93] text-sm">此分類目前沒有通知</p>
        </Block>
      ) : (
        <>
          <BlockTitle>共 {filtered.length} 則通知</BlockTitle>
          <List strongIos insetIos>
            {filtered.map((notification) => {
              const { icon, color } = typeIcons[notification.type]
              const [textColor, bgColor] = color.split(' ')

              return (
                <ListItem
                  key={notification.id}
                  title={
                    <span className="flex items-center gap-2">
                      <span>{notification.title}</span>
                      {!notification.read && (
                        <Badge colors={{ bg: 'bg-[#FF3B30]' }} small />
                      )}
                    </span>
                  }
                  subtitle={notification.message}
                  after={
                    <span className="text-xs text-[#8E8E93] whitespace-nowrap">
                      {formatRelativeTime(notification.timestamp)}
                    </span>
                  }
                  media={
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${textColor} ${bgColor}`}>
                      {icon}
                    </div>
                  }
                  link
                  onClick={() => {
                    if (!notification.read) markAsRead(notification.id)
                  }}
                  className={!notification.read ? 'bg-blue-50/50' : ''}
                />
              )
            })}
          </List>
        </>
      )}
    </Page>
  )
}
